import {BaseQueryApi} from '@reduxjs/toolkit/query/react';
import {addBreadcrumb, captureException} from '@sentry/browser';
import axios from 'axios';
import {AxiosCacheInstance, setupCache} from 'axios-cache-interceptor';
import {isFeatureEnabled} from 'feature-flags';
import flagsmith from 'flagsmith';

// eslint-disable-next-line no-restricted-imports
import {path} from 'ramda';

import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {getWorkspaceFromUri, redirectToLogin, redirectToWorkspace} from '@dms/routes';

import {publicApi} from '../modules/publicApi/publicApi';
import {customRequestInterceptor} from './customRequestInterceptor';
import {getArgsWithAuthorization} from './getArgsWithAuthorization';

const URL_PATH_PREFIX = 'dms';

// Retry configuration
const MAX_RETRY_COUNT = 1;
const RETRY_DELAY_MS = 3000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];

// Track if axios has been initialized
let isAxiosInitialized = false;

export const initializeAxios = (api: BaseQueryApi) => {
  // Only initialize axios once
  if (isAxiosInitialized) {
    return;
  }

  isAxiosInitialized = true;
  axios.defaults.baseURL = `${process.env.API_URL}/${URL_PATH_PREFIX}`;
  axios.interceptors.request.use((config) => {
    const activeBranchId = path(['branch', 'activeBranchId'], api.getState());
    // @ts-ignore
    config.headers = getArgsWithAuthorization({headers: config.headers}, activeBranchId).headers;

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const isRetryEnabled = isFeatureEnabled(featureFlags.RETRY_INTERCEPTOR);
      const responseStatus = Number(error.response?.status) || Number(error.status);

      if (responseStatus >= 500) {
        if (isRetryEnabled) {
          // Check if this request has already been retried
          const retryCount = error.config?.retryCount || 0;

          const isInfrastructureError = INFRASTRUCTURE_ERRORS_CODES.includes(responseStatus);

          if (error.response === undefined || !isInfrastructureError) {
            captureException(error);
          }

          if (isInfrastructureError && retryCount === MAX_RETRY_COUNT) {
            captureException(error);
          }

          // If server error and we haven't exceeded max retries
          if (isInfrastructureError && retryCount < MAX_RETRY_COUNT) {
            addBreadcrumb({
              category: 'api',
              message: `Retrying datagrid API request ${error?.config?.url}, because response status is ${responseStatus}`,
              level: 'error',
            });

            // Increment retry count
            const updatedConfig = {
              ...error.config,
              retryCount: retryCount + 1,
              cache: false,
            };

            // Return a promise that resolves after delay
            return new Promise((resolve) => {
              setTimeout(
                () => {
                  resolve(axios.request(updatedConfig));
                },
                RETRY_DELAY_MS * (retryCount + 1)
              ); // Exponential backoff
            });
          }
        } else {
          captureException(error);
        }
      }

      if (responseStatus === 401) {
        const getRefreshToken = localStorage.getItem(browserStorageKey.REFRESH_TOKEN);
        const {workspace, shouldRedirectToAppWorkspace} = getWorkspaceFromUri();
        const currentUserEmail = path<string>(['user', 'email'], api.getState());

        if (!workspace || shouldRedirectToAppWorkspace) {
          redirectToWorkspace();
          return error;
        }

        if (!getRefreshToken) {
          redirectToLogin({currentUserEmail});
          return error;
        }

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('directLink');

        const refreshTokenArgs = {
          workspace,
          refreshTokenRequestBody: {
            refreshToken: getRefreshToken,
          },
        };

        const token = await api
          .dispatch(publicApi.endpoints.refreshToken.initiate(refreshTokenArgs))
          .unwrap()
          .then(({refreshToken, token}) => {
            sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, token);
            localStorage.setItem(browserStorageKey.REFRESH_TOKEN, refreshToken);

            return token;
          })
          .catch(() => {
            sessionStorage.removeItem(browserStorageKey.ACCESS_TOKEN);
            localStorage.removeItem(browserStorageKey.REFRESH_TOKEN);
            flagsmith.logout();
            redirectToLogin({currentUserEmail});
          });

        return axios.request({
          ...error.config,
          cache: false,
          headers: {...error.config.headers, Authorization: `Bearer ${token}`},
        });
      }

      throw error;
    }
  );

  // eslint-disable-next-line no-restricted-syntax
  if (!(axios as unknown as AxiosCacheInstance).storage) {
    setupCache(axios, {
      // eslint-disable-next-line no-restricted-syntax
      requestInterceptor: customRequestInterceptor(axios as unknown as AxiosCacheInstance),
      ttl: 750,
      cacheTakeover: false,
    });
  }
};
